import * as microsoftTeams from '@microsoft/teams-js';
import { captureException } from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';

interface MsTeamsClient {
  isInitialized: boolean;
  getContext: () => Promise<microsoftTeams.app.Context | null>;
  getAuthToken: () => Promise<string | null>;
  error: Error | null;
}

/**
 * Hook to access Microsoft Teams SDK used for the integration of BEEM inside MS Teams as a app.
 */
const useMsTeamsClient = (): MsTeamsClient => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Initialize the Teams client
  const init = useCallback(async () => {
    if (!isInitialized) {
      try {
        await microsoftTeams.app.initialize();
        setIsInitialized(true);
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      } catch (initError: any) {
        // the only way to check if we are running inside MS Teams is to attempt to init the client,
        // so it is expected to fail here whenever we are outside of MS Teams
        if (initError.message !== 'Initialization Failed. No Parent window found.') {
          captureException(initError);
          setError(initError as Error);
        }
      }
    }
  }, [isInitialized]);

  // Get the Microsoft Teams context
  const getContext = useCallback(async (): Promise<microsoftTeams.app.Context | null> => {
    if (!isInitialized) {
      throw new Error('Teams not initialized!');
    }
    try {
      return await microsoftTeams.app.getContext();
    } catch (contextError) {
      captureException(contextError);
      setError(contextError as Error);
      return null;
    }
  }, [isInitialized]);

  // Get the Microsoft Teams auth token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!isInitialized) {
      throw new Error('Teams not initialized!');
    }
    try {
      return await microsoftTeams.authentication.getAuthToken();
    } catch (authError) {
      captureException(authError);
      setError(authError as Error);
      return null;
    }
  }, [isInitialized]);

  // Ensure initialization happens when the hook is first used
  useEffect(() => {
    init();
  }, [init]);

  return {
    isInitialized,
    getContext,
    getAuthToken,
    error,
  };
};

export default useMsTeamsClient;
